/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace whitespace */

import { Injectable }                  from '@angular/core';
import { HttpClient, HttpParams }                                      from '@angular/common/http';
import { first }                       from "rxjs/operators";

import { HttpHeaders }                 from '@angular/common/http';
import { AuthenticationService }       from './authentication.service';
import { LocomotiveDataHandle }        from "@/models/project/locomotive.data.handle";
import { User }                        from '@/models/user';
import { Logger }                      from "@/util/logger";
import { UserCommandMergeResult }      from "@/models/user.command.merge.result";
import { DiamondCrossingsMergeResult } from "@/models/diamond.crossings.merge.result";


@Injectable({
  providedIn: 'root'
})

/**
 *
 * @class LocoQueryService
 *
 * Remote access to BigQuery via the locoquery service (working with locomotive models)
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      January 2022
 *
 */
export class LocoQueryService {

  private userid: string = '';

  constructor(
    private http: HttpClient,
    private auth: AuthenticationService
  ) {

  }

  /** fetch any headers we need to send along with our request */
  headers(): HttpHeaders {

    const user: User = this.auth.userOfSession;

    if (!user) {

      /* no user logged in */

      return null;
    }

    this.userid = user.username;

    return new HttpHeaders().set('Authorization', `Bearer ${user.token}`);
  }

  /** create a new locomotive model and return its data handle */
  public locoCreate(forecast: string, start: string, end: string): Promise<LocomotiveDataHandle> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    /*
     * return a promise so that they wait for the remote response, and so that we can
     * format the result as a data handle.
     *
     *   http://locoquery/rest/api/1/model/LocomotiveModel
     *     ?userid=CN_USERNAME
     *     &forecast=forecast_2021_27
     *     &start_date=2020-06-01
     *     &end_date=2020-12-01
     *
     */

    return new Promise((resolve, reject) => {

      Logger.debug('[locoCreate] creating model...');

      const params  = new HttpParams().
        set('userid',   this.userid).
        set('forecast', forecast).
        set('start',    start).
        set('end',      end);

      this.http.get(`/backend/locoquery/create/`, {
        headers,
        params
      }).pipe(first())
        .subscribe(
          (result: any) => {

            Logger.debug('[locoCreate] data handle received: ', result);

            /* pass back the data handle */

            resolve(new LocomotiveDataHandle(result));
          },
          (error) => {

            Logger.debug('[locoCreate] remote error: ', error);

            /* bail! */

            reject(error);

          });
    });
  }

  /** run the first time calculation for a model */
  public locoCalculate(datasetId: string): Promise<LocomotiveDataHandle> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return new Promise((resolve, reject) => {

      Logger.debug('[locoCalculate] calculating model...');


      this.http.put(`/backend/locoquery/calculate/${this.userid}/${datasetId}`, {
        headers
      }).pipe(first())
        .subscribe(
          (result: any) => {

            Logger.debug('[locoCalculate] data handle received: ', result);

            /* pass back the data handle */

            resolve(new LocomotiveDataHandle(result));
          },
          (error) => {

            Logger.debug('[locoCalculate] remote error: ', error);

            /* bail! */

            reject(error);

          });
    });
  }


  /** delete a locomotive model */
  public locoDelete(datasetId: string): Promise<string> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return new Promise((resolve, reject) => {

      Logger.debug('[locoDelete] deleting model...');


      this.http.delete(`/backend/locoquery/${datasetId}`, {
        headers
      }).pipe(first())
        .subscribe(
          (result: any) => {

            Logger.debug('[locoDelete] delete response: ', result);

            /* pass back the data handle */

            resolve(datasetId);
          },
          (error) => {

            Logger.debug('[locoDelete] remote delete error: ', error);

            /* bail! */

            reject(error);
          });
    });
  }

  public mergeDiamondCrossings(scenario: string, crossings: string, merged: string): Promise<DiamondCrossingsMergeResult> {

    const headers: HttpHeaders = this.headers();

    if (!headers) {

      return null;
    }

    return new Promise((resolve, reject) => {

      Logger.debug('[mergeDiamondCrossings] building...');

      this.http.post<any>(`/backend/diamond_crossings/merge`, {
        scenario,
        crossings,
        merged,
      },{
        headers
      }).pipe(first())
        .subscribe(
          (result: any) => {

            Logger.debug('[mergeDiamondCrossings] received: ', result);

            /* pass back the pick list */

            if (typeof result.results != 'undefined') {

              result.blob            = result.results['blob'];
              result.merge_time      = result.results['merge_time'];
              result.status          = result.results['status'];
              result.total_time      = result.results['total_time'];
              result.validation_time = result.results['validation_time'];
            }

            resolve(new DiamondCrossingsMergeResult(result));
          },
          (error) => {

            Logger.debug('[mergeDiamondCrossings] remote error: ', error);

            /* bail! */

            reject(error);

          });
    });
  }

  public mergeUserCommand(scenario: string, commands: string, merged: string): Promise<UserCommandMergeResult> {

   const headers: HttpHeaders = this.headers();

    if (!headers) {

      return null;
    }

    return new Promise((resolve, reject) => {

      Logger.debug('[mergeUserCommand] building...');

      this.http.post<any>(`/backend/user_commands/merge`, {
        scenario,
        commands,
        merged,
      },{
        headers
      }).pipe(first())
        .subscribe(
          (result: any) => {

            Logger.debug('[mergeUserCommand] received: ', result);

            /* pass back the pick list */

            if (typeof result.results != 'undefined') {

              result.blob            = result.results['blob'];
              result.merge_time      = result.results['merge_time'];
              result.status          = result.results['status'];
              result.total_time      = result.results['total_time'];
              result.validation_time = result.results['validation_time'];
            }

            resolve(new UserCommandMergeResult(result));
          },
          (error) => {

            Logger.debug('[mergeUserCommand] remote error: ', error);

            /* bail! */

            reject(error);

          });
    });
  }

  /** generate batch regression model */
  public historicalRegression(corridor: string, start: string, end: string, folder: string): Promise<{key: string}> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return new Promise((resolve, reject) => {

      Logger.debug('[historicalRegression] building...');

      this.http.post<any>(`/backend/operating_measures/historical/regression`, {
        corridor,
        start,
        end,
        folder
      },{
        headers
      }).pipe(first())
        .subscribe(
          (result: any) => {

            Logger.debug('[historicalRegression] received: ', result);

            /* pass back the pick list */

            resolve({key: result.key});
          },
          (error) => {

            Logger.debug('[historicalRegression] remote error: ', error);

            /* bail! */

            reject(error);

          });
    });
  }

  /** generate a fake scenario and train movement from historical data */
  public historicalStringline(corridor: string, start: string, end: string, folder: string): Promise<{scenario: string, movement: string}> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return new Promise((resolve, reject) => {

      Logger.debug('[historicalStringline] building...');

      this.http.post<any>(`/backend/operating_measures/historical/stringline`, {
        corridor,
        start,
        end,
        folder
      },{
        headers
      }).pipe(first())
        .subscribe(
          (result: any) => {

            Logger.debug('[historicalStringline] received: ', result);

            /* pass back the pick list */

            resolve({scenario: result.input, movement: result.output});
          },
          (error) => {

            Logger.debug('[historicalStringline] remote error: ', error);

            /* bail! */

            reject(error);

          });
    });
  }

  /** generate the list of corridors to use for operational measures */
  public corridorList(): Promise<string[]> {

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    return new Promise((resolve, reject) => {

      Logger.debug('[corridorList] fetching corridor pick list...');

      this.http.get<string>(`/backend/operating_measures/corridors`, {
        headers
      }).pipe(first())
        .subscribe(
          (result: any) => {

            Logger.debug('[corridorList] received: ', result);

            /* pass back the pick list */

            resolve(result);
          },
          (error) => {

            Logger.debug('[corridorList] remote error: ', error);

            /* bail! */

            reject(error);

          });
    });
  }


}
